import { Fragment, useState } from 'react';
import { BALANCE_SUBSECTIONS, formatJMD } from '../../lib/finances';
import { toDateInputValue } from '../../lib/period';
import { buildBalanceSheet, retainedEarningsThrough } from '../../lib/financeReports';

const BLANK = {
  as_of_date: '',
  section: 'Asset',
  subsection: 'Current',
  label: '',
  amount_jmd: '',
  notes: '',
};

function SectionBlock({ title, rows, total, totalLabel, onDelete, extraRow }) {
  const withSub = (sub) => rows.filter((r) => r.subsection === sub);

  return (
    <>
      <tr className="pnl-section-head">
        <td colSpan={2}>{title}</td>
        <td />
      </tr>

      {BALANCE_SUBSECTIONS.map((sub) => {
        const inSub = withSub(sub);
        if (!inSub.length) return null;
        return (
          <Fragment key={`${title}-${sub}`}>
            <tr className="bs-subsection">
              <td colSpan={3}>{sub}</td>
            </tr>
            {inSub.map((row) => (
              <tr key={row.id}>
                <td className="pnl-indent">
                  {row.label}
                  {row.notes && <span className="bs-note"> — {row.notes}</span>}
                </td>
                <td className="entry-table-numeric">{formatJMD(row.amount)}</td>
                <td className="bs-actions">
                  <button className="entry-table-delete" onClick={() => onDelete(row.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </Fragment>
        );
      })}

      {/* Equity items carry no current/non-current split, so they render flat. */}
      {withSub(null).map((row) => (
        <tr key={row.id}>
          <td className="pnl-indent">
            {row.label}
            {row.notes && <span className="bs-note"> — {row.notes}</span>}
          </td>
          <td className="entry-table-numeric">{formatJMD(row.amount)}</td>
          <td className="bs-actions">
            <button className="entry-table-delete" onClick={() => onDelete(row.id)}>
              Delete
            </button>
          </td>
        </tr>
      ))}

      {extraRow}

      {rows.length === 0 && !extraRow && (
        <tr>
          <td className="pnl-indent pnl-none">Nothing recorded</td>
          <td className="entry-table-numeric">{formatJMD(0)}</td>
          <td />
        </tr>
      )}

      <tr className="pnl-subtotal">
        <td>{totalLabel}</td>
        <td className="entry-table-numeric">{formatJMD(total)}</td>
        <td />
      </tr>
    </>
  );
}

export default function BalanceSheet({ items, income, expenses, asOf, asOfLabel, onAdd, onDelete }) {
  const [form, setForm] = useState({ ...BLANK, as_of_date: toDateInputValue(asOf) });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A balance sheet is a point in time, not a span — it's always "as at" the
  // end of whatever period is selected.
  const retained = retainedEarningsThrough(income, expenses, asOf);
  const sheet = buildBalanceSheet(items, asOf, retained);
  const balanced = Math.abs(sheet.difference) < 0.01;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onAdd({
        as_of_date: form.as_of_date,
        section: form.section,
        // Equity has no current/non-current concept — send null, matching
        // the nullable column rather than storing a meaningless value.
        subsection: form.section === 'Equity' ? null : form.subsection,
        label: form.label.trim(),
        amount_jmd: Number(form.amount_jmd || 0),
        notes: form.notes.trim() || null,
      });
      setForm({ ...BLANK, as_of_date: toDateInputValue(asOf) });
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pnl">
      <div className="pnl-statement-head">
        <h3>Balance Sheet</h3>
        <p>Skin Fairy JA Co. Ltd. — as at {asOfLabel}</p>
      </div>

      <table className="entry-table pnl-table">
        <tbody>
          <SectionBlock
            title="Assets"
            rows={sheet.assets}
            total={sheet.assetsTotal}
            totalLabel="Total assets"
            onDelete={onDelete}
          />
          <SectionBlock
            title="Liabilities"
            rows={sheet.liabilities}
            total={sheet.liabilitiesTotal}
            totalLabel="Total liabilities"
            onDelete={onDelete}
          />
          <SectionBlock
            title="Equity"
            rows={sheet.equityItems}
            total={sheet.equityTotal}
            totalLabel="Total equity"
            onDelete={onDelete}
            extraRow={
              <tr key="retained">
                <td className="pnl-indent">
                  Retained earnings
                  <span className="bs-note"> — computed from all income and expenses to date</span>
                </td>
                <td className="entry-table-numeric">{formatJMD(retained)}</td>
                <td />
              </tr>
            }
          />

          <tr className="pnl-highlight">
            <td>Total liabilities and equity</td>
            <td className="entry-table-numeric">{formatJMD(sheet.liabilitiesAndEquity)}</td>
            <td />
          </tr>
        </tbody>
      </table>

      <div className={'bs-balance' + (balanced ? ' bs-balance-ok' : ' bs-balance-off')}>
        {balanced ? (
          <>Balanced — assets equal liabilities plus equity.</>
        ) : (
          <>
            Out of balance by {formatJMD(Math.abs(sheet.difference))}. Assets{' '}
            {sheet.difference > 0 ? 'exceed' : 'fall short of'} liabilities plus equity — something is
            missing or double-counted.
          </>
        )}
      </div>

      {open ? (
        <form className="bs-form" onSubmit={handleSubmit}>
          <div className="bs-form-grid">
            <label>
              As of date
              <input type="date" required value={form.as_of_date} onChange={set('as_of_date')} />
            </label>
            <label>
              Section
              <select value={form.section} onChange={set('section')}>
                <option value="Asset">Asset</option>
                <option value="Liability">Liability</option>
                <option value="Equity">Equity</option>
              </select>
            </label>
            {form.section !== 'Equity' && (
              <label>
                Type
                <select value={form.subsection} onChange={set('subsection')}>
                  {BALANCE_SUBSECTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Label
              <input
                required
                placeholder="e.g. Bank account, Inventory, Supplier credit"
                value={form.label}
                onChange={set('label')}
              />
            </label>
            <label>
              Amount (JMD)
              <input
                type="number"
                step="0.01"
                required
                value={form.amount_jmd}
                onChange={set('amount_jmd')}
              />
            </label>
            <label className="bs-form-wide">
              Notes (optional)
              <input value={form.notes} onChange={set('notes')} />
            </label>
          </div>

          {error && <p className="finances-error">{error}</p>}

          <div className="bs-form-actions">
            <button type="button" className="bs-form-cancel" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Add line item'}
            </button>
          </div>
        </form>
      ) : (
        <button className="bs-add-button" onClick={() => setOpen(true)}>
          + Add balance sheet line
        </button>
      )}

      <p className="pnl-note">
        Unlike the P&amp;L, a balance sheet can't be derived from income and expenses alone — record
        what the business owns and owes here. Each line is an "as of" snapshot: to correct a figure,
        add a newer one for the same label rather than editing history. Retained earnings is always
        computed, so don't enter it as a line.
      </p>
    </div>
  );
}
